import React from "react";
import { useAuth } from "@/lib/auth";
import { Navigate, Link } from "react-router-dom";
import { Clock, Mail, ShieldCheck, LogOut, Radar } from "lucide-react";

export default function WaitlistPage() {
  const { user, logout } = useAuth();

  if (!user) return <Navigate to="/login" replace />;
  // Already approved? Bounce to the right place.
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.approval_status === "approved") {
    return user.subscription_status === "active"
      ? <Navigate to="/app" replace />
      : <Navigate to="/billing" replace />;
  }

  const rejected = user.approval_status === "rejected";

  return (
    <div className="min-h-screen bg-[#050505] grain text-white flex flex-col">
      <header className="border-b border-[#262626] bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="waitlist-home">
            <div className="w-7 h-7 border border-[#EAB308] flex items-center justify-center bg-black">
              <Radar className="w-4 h-4 text-[#EAB308]" />
            </div>
            <div className="font-heading text-base font-black tracking-tighter uppercase">
              Card<span className="text-[#EAB308]">Radar</span>
            </div>
          </Link>
          <button
            data-testid="waitlist-logout"
            onClick={logout}
            className="px-3 py-2 text-xs uppercase tracking-wider font-bold border border-[#262626] hover:border-[#EAB308] text-white flex items-center gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full" data-testid="waitlist-card">
          <div className="bg-[#0d0d0d] border border-[#262626] rounded-sm p-8">
            <div className={`w-12 h-12 border flex items-center justify-center mb-5 ${
              rejected
                ? "border-[#EF4444] bg-[#EF4444]/10"
                : "border-[#EAB308] bg-[#EAB308]/10"
            }`}>
              {rejected
                ? <ShieldCheck className="w-6 h-6 text-[#EF4444]" />
                : <Clock className="w-6 h-6 text-[#EAB308]" />}
            </div>

            {rejected ? (
              <>
                <h1 className="font-heading text-3xl font-black uppercase tracking-tighter mb-2">
                  Access denied
                </h1>
                <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
                  Your account was not approved for CardRadar. If you believe this is a
                  mistake, reply to your original signup email or reach out to support.
                </p>
              </>
            ) : (
              <>
                <h1 className="font-heading text-3xl font-black uppercase tracking-tighter mb-2">
                  You&apos;re on the <span className="text-[#EAB308]">waitlist</span>
                </h1>
                <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
                  Thanks for signing up. To keep the bot fast and the auto-buy queue
                  uncontested, every new account is reviewed by an admin before it can
                  subscribe or run scans. We&apos;ll get to you shortly.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3 p-3 border border-[#262626] rounded-sm bg-[#050505]">
                    <Mail className="w-4 h-4 text-[#EAB308] flex-shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <div className="text-white font-bold uppercase tracking-wider mb-0.5">Account on file</div>
                      <div className="text-[#A3A3A3] font-mono" data-testid="waitlist-email">{user.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 border border-[#262626] rounded-sm bg-[#050505]">
                    <ShieldCheck className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <div className="text-white font-bold uppercase tracking-wider mb-0.5">What unlocks on approval</div>
                      <ul className="text-[#A3A3A3] space-y-0.5 mt-1 list-disc list-inside marker:text-[#525252]">
                        <li>Subscribe ($200/mo) and start scanning</li>
                        <li>Activate the desktop <code className="text-[#EAB308]">.exe</code> for live auto-buy</li>
                        <li>Build Smart Action pipelines (Scalp mode)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#525252] border-t border-[#262626] pt-3">
              Status: <span className={rejected ? "text-[#EF4444]" : "text-[#EAB308]"}>
                {user.approval_status}
              </span>
            </div>
          </div>

          <p className="text-center text-[11px] text-[#525252] mt-4">
            You&apos;ll be redirected automatically once your account is reviewed.
          </p>
        </div>
      </main>
    </div>
  );
}
